import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Trash2, MessageCircle, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import type { ChatMessage } from '../../types';
import { Button, Card, ConfirmModal, Skeleton, TextArea } from '../ui';

interface CaseAssistantTabProps {
  caseId: string;
}

const SUGGESTION_CHIPS = [
  'Summarize the community property transactions',
  'What are the largest waste claims?',
  'Compare husband and wife spending patterns',
  'List all separate property transactions',
  'What accounts have reconciliation issues?',
  'Explain the property classification rules',
];

const CHIP_CLASS =
  'h-auto whitespace-normal rounded-full border-cream-dark bg-cream py-1 text-left text-sm font-normal text-green-800 hover:border-green-300 hover:bg-green-100';

export default function CaseAssistantTab({ caseId }: CaseAssistantTabProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent, scrollToBottom]);

  useEffect(() => {
    loadHistory();
  }, [caseId]);

  async function loadHistory() {
    setIsLoading(true);
    try {
      const res = await api.get(`/cases/${caseId}/chat`);
      setMessages(res.data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }

  async function sendMessage(text: string) {
    if (!text.trim() || isStreaming) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      case_id: caseId,
      role: 'user',
      content: text.trim(),
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setStreamingContent('');

    try {
      const response = await fetch(`/api/cases/${caseId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              const assistantMessage: ChatMessage = {
                id: Date.now() + 1,
                case_id: caseId,
                role: 'assistant',
                content: accumulated,
                created_at: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingContent('');
              break;
            }
            accumulated += data;
            setStreamingContent(accumulated);
          }
        }
      }
    } catch {
      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        case_id: caseId,
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setStreamingContent('');
    } finally {
      setIsStreaming(false);
    }
  }

  async function clearHistory() {
    try {
      await api.delete(`/cases/${caseId}/chat`);
      setMessages([]);
    } catch {
      // ignore
    } finally {
      setConfirmClearOpen(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading chat history"
        className="flex h-64 flex-col justify-center gap-3 px-6"
      >
        <Skeleton variant="rect" className="h-10 w-1/2 self-end rounded-lg" />
        <Skeleton variant="rect" className="h-16 w-2/3 rounded-lg" />
        <Skeleton variant="rect" className="h-10 w-2/5 self-end rounded-lg" />
        <span className="sr-only">Loading chat history</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-280px)]">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle size={20} className="text-green-700" />
          <h2 className="t-section text-green-900">Case Assistant</h2>
        </div>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            onClick={() => setConfirmClearOpen(true)}
            disabled={isStreaming}
          >
            Clear History
          </Button>
        )}
      </div>

      <ConfirmModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirm={clearHistory}
        title="Clear chat history?"
        message="Every message in this case's assistant conversation will be permanently deleted."
        confirmLabel="Clear history"
        variant="danger"
      />

      <Card padding={false} className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && !isStreaming && (
            <div className="flex h-full flex-col items-center justify-center py-12 text-center">
              <Sparkles size={40} className="mb-4 text-green-600" />
              <h3 className="t-card mb-2 text-green-900">
                How can I help with this case?
              </h3>
              <p className="mb-6 max-w-md text-ink-faint">
                Ask me about transactions, property classifications, waste claims, or anything else
                about this case.
              </p>
              <div className="flex max-w-lg flex-wrap justify-center gap-2">
                {SUGGESTION_CHIPS.map((chip) => (
                  <Button
                    key={chip}
                    variant="secondary"
                    size="sm"
                    onClick={() => sendMessage(chip)}
                    className={CHIP_CLASS}
                  >
                    {chip}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3/4 rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-green-900 text-white rounded-br-md'
                    : 'bg-cream border border-cream-dark text-green-900 rounded-bl-md'
                }`}
              >
                <p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>
                <p
                  className={`mt-1 text-xs ${
                    msg.role === 'user' ? 'text-green-200' : 'text-ink-faint'
                  }`}
                >
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}

          {isStreaming && streamingContent && (
            <div className="flex justify-start">
              <div className="max-w-3/4 rounded-2xl rounded-bl-md border border-cream-dark bg-cream px-4 py-3 text-green-900">
                <p className="whitespace-pre-wrap break-words text-sm">{streamingContent}</p>
              </div>
            </div>
          )}

          {isStreaming && !streamingContent && (
            <div className="flex justify-start">
              <div className="max-w-3/4 rounded-2xl rounded-bl-md border border-cream-dark bg-cream px-4 py-3 text-green-900">
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-green-600" style={{ animationDelay: '0ms' }} />
                  <div className="h-2 w-2 animate-bounce rounded-full bg-green-600" style={{ animationDelay: '150ms' }} />
                  <div className="h-2 w-2 animate-bounce rounded-full bg-green-600" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-cream-dark bg-cream-light p-4">
          {messages.length > 0 && !isStreaming && (
            <div className="mb-3 flex flex-wrap gap-2">
              {SUGGESTION_CHIPS.slice(0, 3).map((chip) => (
                <Button
                  key={chip}
                  variant="secondary"
                  size="sm"
                  onClick={() => sendMessage(chip)}
                  className={`${CHIP_CLASS} bg-cream-light text-xs hover:bg-green-50`}
                >
                  {chip}
                </Button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <TextArea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about this case..."
                rows={1}
                disabled={isStreaming}
                className="min-h-9 resize-none rounded-xl px-4"
                style={{ maxHeight: '120px' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
            </div>
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isStreaming}
              size="lg"
              icon={Send}
              aria-label="Send message"
              className="rounded-xl"
            />
          </div>
          <p className="mt-2 text-center text-xs text-ink-faint">
            Shale can make mistakes. Check important info.
          </p>
        </div>
      </Card>
    </div>
  );
}
